import type { Nullable } from '@iheartjane/types';
import { useCallback, useEffect, useState } from 'react';
import type { Fetcher } from 'react-router';
import { useFetcher, useFetchers } from 'react-router';

import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { CardLocation } from '@jane/bloom/services/tracking/Events';
import { trackAddedProductToCart } from '@jane/bloom/services/tracking/track';
import type { ActionResult } from '@jane/bloom/types/remixAction';
import type { ProductViewModel } from '@jane/bloom/viewModels/productDetail';
import type { ProductSearchResultViewModel } from '@jane/bloom/viewModels/productSearch';

interface AddToCartConfigBase {
  productId: number;
  rowId?: number | string;
}

export interface AddToCartFormArgs {
  action: string;
  fetcherKey: string;
  method: 'post';
  navigate: boolean;
  preventScrollReset: boolean;
}

interface AddToCartConfig extends AddToCartConfigBase {
  cardLocation?: CardLocation;
  index?: number;
  product: ProductSearchResultViewModel | ProductViewModel;
  quantity: number;
  selectedWeight?: Nullable<string> | undefined;
}

const FETCHER_KEY_PREFIX = 'add-to-cart';
const fetcherKey = ({ productId, rowId }: AddToCartConfigBase) =>
  `${FETCHER_KEY_PREFIX}-${productId}-row-${rowId ?? ''}`;

export const buildAddToCartFormArgs = ({
  productId,
  rowId,
}: AddToCartConfigBase): AddToCartFormArgs => ({
  action: `/api/add-to-cart`,
  fetcherKey: fetcherKey({ productId, rowId }),
  method: 'post' as const,
  navigate: false,
  preventScrollReset: true,
});

/** True when an add-to-cart fetcher key belongs to the given product (any row). */
export const isAddToCartFetcherFor = (key: string, productId: number) =>
  key.startsWith(fetcherKey({ productId }));

export const useAddToCartFetchers = (
  productId?: number
): (Fetcher<ActionResult> & { key: string })[] => {
  const fetchers = useFetchers();
  return fetchers.filter(({ key }) =>
    productId === undefined
      ? key.startsWith(FETCHER_KEY_PREFIX)
      : isAddToCartFetcherFor(key, productId)
  );
};

export const useAddToCartConfig = ({
  cardLocation,
  index,
  product,
  productId,
  quantity = 1,
  rowId,
  selectedWeight,
}: AddToCartConfig) => {
  const fetcher = useFetcher<ActionResult>({
    key: fetcherKey({ productId, rowId }),
  });
  const { cart, featureFlags } = useRootLoaderData();
  const { fulfillmentMode, searchReservationMode } = useStoreSwitcher();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const trackAddToCart = useCallback(() => {
    trackAddedProductToCart(
      {
        cardLocation,
        cart,
        fulfillmentMode,
        index,
        product,
        quantity,
        reservationMode: searchReservationMode,
        selectedWeight,
      },
      featureFlags
    );
  }, [
    cardLocation,
    cart,
    featureFlags,
    fulfillmentMode,
    index,
    product,
    quantity,
    searchReservationMode,
    selectedWeight,
  ]);

  /**
   * Track add to cart event when fetcher state changes to idle after submitting.
   * This is to ensure that the event is tracked after the cart is updated.
   * Only successful adds count — a rejected add (e.g. an unavailable product)
   * never made it into the cart.
   */
  useEffect(() => {
    if (fetcher.state !== 'idle' || !hasSubmitted) return;

    setHasSubmitted(false);
    if (fetcher.data?.success) trackAddToCart();
  }, [fetcher.data, fetcher.state, hasSubmitted, trackAddToCart]);

  useEffect(() => {
    if (fetcher.state === 'submitting') {
      setHasSubmitted(true);
    }
  }, [fetcher.state]);

  return {
    fetcher,
    /**
     * Fetcher props to add to a Remix Form or Submit function.
     */
    formArgs: buildAddToCartFormArgs({ productId, rowId }),
  };
};
